import React from 'react';

// Canonical status values written by the backend FPL sync (see app/core/fpl_sync.py):
//   "injured" | "doubt" | "unavailable" | "suspended"
//
// Rendered as compact text pills so the category is readable at a glance without
// requiring the reader to decode an icon:
//   injured     -> INJ   (red)
//   doubt       -> QUES  (yellow — conveys uncertainty, not a hard-out)
//   unavailable -> OUT   (red)
//   suspended   -> SUSP  (red)

const LABEL_BY_STATUS: Record<string, string> = {
  injured: 'INJ',
  doubt: 'QUES',
  unavailable: 'OUT',
  suspended: 'SUSP',
};

const COLOR_CLASSES_BY_STATUS: Record<string, string> = {
  injured: 'bg-red-500/20 text-red-400',
  doubt: 'bg-accent-yellow/20 text-accent-yellow',
  unavailable: 'bg-red-500/20 text-red-400',
  suspended: 'bg-red-500/20 text-red-400',
};

interface InjuryBadgeProps {
  status: string | null | undefined;
  /**
   * Free-text detail blurb (e.g. "75% chance - Knock from Saturday - Assessed in training").
   * Falls back to the raw status code-word if null. Powers the hover tooltip and aria-label,
   * so readers get the actual injury context, not just the category.
   */
  details?: string | null;
  /** Kept for backwards compatibility with the old icon API; ignored for text pills. */
  size?: number;
  /** Additional tailwind classes on the wrapping span. */
  className?: string;
  /**
   * Override the auto-selected color classes (e.g. `'bg-red-500/20 text-red-400'`).
   * Use in contexts where the default "doubt" yellow clashes with nearby UI.
   */
  colorClass?: string;
}

const InjuryBadge: React.FC<InjuryBadgeProps> = ({ status, details, className = '', colorClass }) => {
  if (!status) return null;
  const label = LABEL_BY_STATUS[status];
  if (!label) return null;
  const resolvedColorClasses = colorClass ?? (COLOR_CLASSES_BY_STATUS[status] || 'bg-red-500/20 text-red-400');
  const tooltip = (typeof details === 'string' && details.trim().length > 0) ? details.trim() : status;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-micro sm:text-badge font-bold tracking-wider ${resolvedColorClasses} ${className}`}
      aria-label={`Injury status: ${tooltip}`}
      title={tooltip}
    >
      {label}
    </span>
  );
};

export default InjuryBadge;
